import { useState } from 'react';
import styled from 'styled-components';
import type { ContractEstimateOption, KsPackage } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import { Combobox } from '@shared/ui/Combobox';
import {
  fmtMoney,
  packageStatusLabel,
  packageStatusTone,
} from '@shared/lib/smetaLabels';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Inline = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const PackageRow = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border: 1px solid
  ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  font: inherit;
  color: inherit;
  transition: border-color 120ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const PackageCountMuted = styled(Muted)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const PeriodField = styled(Field)`
  flex: 1;
`;

const CreateBtnWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const TopField = styled(Field)`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

function packageLabel(p: KsPackage): string {
  const contract = p.contract_no ? `дог. № ${p.contract_no}` : (p.contract_label ?? 'договор');
  return `КС-3 № ${p.ks3_no} · ${contract}`;
}

export interface KsPackageSidebarProps {
  packages: KsPackage[];
  selectedId: string | null;
  onSelectPackage: (id: string) => void;
  canWrite: boolean;
  creating: boolean;
  contextContracts: { id: string; contract_number?: string | null; counterparty?: string | null; subject?: string | null }[];
  contextProjects: { id: string; name: string; code?: string }[];
  estimateOptions: ContractEstimateOption[];
  activeProjectId?: string | null;
  setActiveProject: (id: string | null, opts?: { name?: string; code?: string }) => void;
  onCreatePackage: (payload: {
    contract_estimate_id?: string;
    contract_id: string;
    project_id?: string;
    ks3_no: string;
    doc_date: string;
    period_from: string;
    period_to: string;
  }) => Promise<void>;
  createError: string | null;
}

export function KsPackageSidebar({
  packages,
  selectedId,
  onSelectPackage,
  canWrite,
  creating,
  contextContracts,
  contextProjects,
  estimateOptions,
  activeProjectId,
  setActiveProject,
  onCreatePackage,
  createError,
}: KsPackageSidebarProps) {
  const [estimateId, setEstimateId] = useState('');
  const [estimateLabel, setEstimateLabel] = useState('');
  const [directContractId, setDirectContractId] = useState('');
  const [projectId, setProjectId] = useState(activeProjectId ?? '');
  const [ks3No, setKs3No] = useState('');
  const [docDate, setDocDate] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');

  const comboboxItems = estimateOptions.map((o) => {
    const num = o.contract_number ? `№ ${o.contract_number}` : 'договор без №';
    const who = (o.subject?.trim() || o.counterparty || '').slice(0, 60);
    const ver = `в.${o.version_no}${o.status === 'active' ? ' (активная)' : ` (${o.status})`}`;
    return {
      id: o.id,
      label: `${num} · ${who} · ${ver} · ${o.item_count} поз.`,
    };
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const selectedOpt = estimateOptions.find((o) => o.id === estimateId);
    const resolvedContractId = selectedOpt?.contract_id || directContractId;
    await onCreatePackage({
      contract_estimate_id: estimateId || undefined,
      contract_id: resolvedContractId,
      project_id: projectId || undefined,
      ks3_no: ks3No,
      doc_date: docDate,
      period_from: periodFrom,
      period_to: periodTo,
    });
  };

  return (
    <Stack>
      <Card>
        <Title>Новый пакет КС</Title>
        <Hint>
          Пакет объединяет формы одного расчётного периода: КС-3, акты КС-2, журнал КС-6а, реестры и
          отраслевые ведомости списания (М-19, М-29, ВВМП/ВВДМ, ФОТ).
        </Hint>

        <form onSubmit={handleCreate}>
          <TopField>
            <Label>Эталон сметы договора (мир A)</Label>
            <Combobox
              options={comboboxItems}
              value={estimateId}
              selectedLabel={estimateLabel}
              onChange={(opt) => {
                setEstimateId(opt?.id ?? '');
                setEstimateLabel(opt?.label ?? '');
                if (opt?.id) setDirectContractId('');
              }}
              placeholder="Поиск по номеру договора или контрагенту…"
            />
            <Hint>
              Привязка к утверждённой версии сметы. Из неё берутся расценки, лимиты объёмов и
              наименования позиций.
            </Hint>
          </TopField>

          <Field>
            <Label>Либо договор напрямую (мир B / сводный профиль)</Label>
            <Select
              value={directContractId}
              onChange={(e) => {
                setDirectContractId(e.target.value);
                if (e.target.value) {
                  setEstimateId('');
                  setEstimateLabel('');
                }
              }}
            >
              <option value="">— не выбран —</option>
              {contextContracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.contract_number ? `№ ${c.contract_number}` : 'без номера'}
                  {c.counterparty ? ` · ${c.counterparty}` : ''}
                </option>
              ))}
            </Select>
            <Hint>
              Для договоров с несколькими локальными сметами (ЛСР) либо без загруженного эталона
              сметы.
            </Hint>
          </Field>

          <Field>
            <Label>Проект</Label>
            <Select
              value={projectId}
              onChange={(e) => {
                const pid = e.target.value;
                setProjectId(pid);
                const p = contextProjects.find((x) => x.id === pid);
                setActiveProject(pid || null, { name: p?.name, code: p?.code });
              }}
            >
              <option value="">— авто / по договору —</option>
              {contextProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code ? `${p.code} · ` : ''}
                  {p.name}
                </option>
              ))}
            </Select>
            <Hint>
              Привязка к проекту ПТО позволяет сборщику втягивать принятые записи журнала ПТО и
              рейсы Логиста по объекту.
            </Hint>
          </Field>
          <Field>
            <Label>Номер КС-3</Label>
            <Input value={ks3No} onChange={(e) => setKs3No(e.target.value)} placeholder="21" />
            <Hint>
              Номер вместе с договором — ключ пакета: повторное заведение с тем же номером
              обновит шапку, а не заведёт второй пакет.
            </Hint>
          </Field>
          <Field>
            <Label>Дата документа</Label>
            <Input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} />
          </Field>
          <Inline>
            <PeriodField>
              <Label>Период с</Label>
              <Input
                type="date"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
              />
            </PeriodField>
            <PeriodField>
              <Label>по</Label>
              <Input
                type="date"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
              />
            </PeriodField>
          </Inline>
          {createError && <Err>{createError}</Err>}
          <CreateBtnWrapper>
            <Button
              type="submit"
              $variant="primary"
              disabled={creating || !canWrite}
              title={!canWrite ? 'Только чтение' : undefined}
            >
              {creating ? 'Завожу…' : 'Завести пакет'}
            </Button>
          </CreateBtnWrapper>
        </form>
      </Card>

      <div>
        <PackageCountMuted>Пакетов: {packages.length}</PackageCountMuted>
        <Stack>
          {packages.map((p) => (
            <PackageRow
              key={p.id}
              $active={p.id === selectedId}
              onClick={() => onSelectPackage(p.id)}
            >
              <Row>
                <Title>{packageLabel(p)}</Title>
                <Badge $tone={packageStatusTone(p.status)}>
                  {packageStatusLabel(p.status)}
                </Badge>
              </Row>
              <Muted>
                {p.period_from} — {p.period_to}
                {p.total_period !== null ? ` · ${fmtMoney(p.total_period)} ₽` : ''}
              </Muted>
            </PackageRow>
          ))}
          {packages.length === 0 && <Muted>Пока нет пакетов — заведите первый выше.</Muted>}
        </Stack>
      </div>
    </Stack>
  );
}
